<template>
  <BaseResponseComponent :response="response">
    <template #default="{ response: responseData }">
      <div class="schedule-search-results">
        <!-- Search metadata -->
        <div v-if="responseData.metadata" class="search-metadata">
          <div class="metadata-row">
            <span class="metadata-label">Search:</span>
            <span class="metadata-value">{{ responseData.metadata.query }}</span>
          </div>
          <div class="metadata-row">
            <span class="metadata-label">Found:</span>
            <span class="metadata-value">{{ responseData.metadata.total_found }} session{{ responseData.metadata.total_found !== 1 ? 's' : '' }}</span>
            <span v-if="responseData.metadata.search_time_ms" class="search-time">
              ({{ responseData.metadata.search_time_ms }}ms)
            </span>
          </div>
          <div v-if="responseData.metadata.filters_applied && responseData.metadata.filters_applied.length > 0" class="metadata-row">
            <span class="metadata-label">Filters:</span>
            <span class="metadata-value">{{ responseData.metadata.filters_applied.join(', ') }}</span>
          </div>
          <div v-if="responseData.metadata.date_range" class="metadata-row">
            <span class="metadata-label">Date Range:</span>
            <span class="metadata-value">{{ responseData.metadata.date_range }}</span>
          </div>
        </div>

        <!-- Quick filters/grouping options -->
        <div v-if="responseData.grouping_options" class="grouping-options">
          <div class="grouping-label">Group by:</div>
          <div class="grouping-buttons">
            <button
              v-for="option in responseData.grouping_options"
              :key="option.value"
              @click="handleGroupingChange(option.value)"
              class="grouping-btn"
              :class="{ active: currentGrouping === option.value }"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <!-- Results list -->
        <div v-if="responseData.results && responseData.results.length > 0" class="results-container">
          <div class="results-header">
            <h4>Schedule Results</h4>
            <div v-if="responseData.ui_suggestions" class="display-options">
              <span class="display-type">{{ getDisplayTypeLabel(responseData.ui_suggestions.display_type) }}</span>
            </div>
          </div>
          
          <!-- Group sessions by current grouping -->
          <div v-if="currentGrouping && currentGrouping !== 'none'" class="grouped-results">
            <div 
              v-for="(group, groupName) in getGroupedSessions(responseData.results)"
              :key="groupName"
              class="session-group"
            >
              <h5 class="group-title">
                <span class="group-icon">
                  <svg v-if="currentGrouping === 'time'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12,6 12,12 16,14"></polyline>
                  </svg>
                  <svg v-else-if="currentGrouping === 'location'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                  </svg>
                </span>
                {{ groupName }}
                <span class="group-count">({{ group.length }})</span>
              </h5>
              <div class="group-sessions">
                <SessionCard
                  v-for="session in group"
                  :key="session.id"
                  :session="session"
                  :timezone="responseData.metadata?.timezone || 'UTC'"
                  @action-click="handleSessionAction"
                  @session-click="handleSessionClick"
                  @speaker-click="handleSpeakerClick"
                  @tag-click="handleTagClick"
                />
              </div>
            </div>
          </div>

          <!-- Ungrouped results -->
          <div v-else class="ungrouped-results">
            <div class="results-list" :class="getDisplayClass(responseData.ui_suggestions)">
              <SessionCard
                v-for="session in responseData.results"
                :key="session.id"
                :session="session"
                :timezone="responseData.metadata?.timezone || 'UTC'"
                @action-click="handleSessionAction"
                @session-click="handleSessionClick"
                @speaker-click="handleSpeakerClick"
                @tag-click="handleTagClick"
              />
            </div>
          </div>
        </div>

        <!-- No results message -->
        <div v-else-if="responseData.metadata && responseData.metadata.total_found === 0" class="no-results">
          <div class="no-results-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
              <line x1="12" y1="14" x2="12" y2="14.01"></line>
            </svg>
          </div>
          <h4>No sessions found</h4>
          <p>Try searching with different keywords, time ranges, or topics.</p>
        </div>

        <!-- Schedule insights -->
        <div v-if="responseData.schedule_insights" class="schedule-insights">
          <h5 class="insights-title">Schedule Insights</h5>
          <div class="insights-grid">
            <div v-if="responseData.schedule_insights.peak_times" class="insight-item">
              <strong>Peak Times:</strong>
              <span>{{ responseData.schedule_insights.peak_times.join(', ') }}</span>
            </div>
            <div v-if="responseData.schedule_insights.popular_tracks" class="insight-item">
              <strong>Popular Tracks:</strong>
              <span>{{ responseData.schedule_insights.popular_tracks.join(', ') }}</span>
            </div>
            <div v-if="responseData.schedule_insights.recommended_sessions" class="insight-item">
              <strong>Recommended:</strong>
              <span>{{ responseData.schedule_insights.recommended_sessions.length }} sessions based on your interests</span>
            </div>
          </div>
        </div>

        <!-- Follow-up suggestions -->
        <div v-if="responseData.ui_suggestions && responseData.ui_suggestions.suggested_actions" class="follow-up-actions">
          <SuggestedActions 
            :actions="responseData.ui_suggestions.suggested_actions"
            @action-click="handleFollowUpAction"
          />
        </div>
      </div>
    </template>
  </BaseResponseComponent>
</template>

<script>
import BaseResponseComponent from './BaseResponseComponent.vue';
import SessionCard from './SessionCard.vue';
import SuggestedActions from './SuggestedActions.vue';

export default {
  name: 'ScheduleSearchResults',
  components: {
    BaseResponseComponent,
    SessionCard,
    SuggestedActions
  },
  props: {
    response: {
      type: Object,
      required: true
    }
  },
  emits: ['action-click', 'session-action', 'session-click', 'speaker-click', 'tag-click'],
  data() {
    return {
      currentGrouping: 'none'
    };
  },
  methods: {
    getDisplayTypeLabel(displayType) {
      const typeLabels = {
        'schedule_grid': 'Grid View',
        'schedule_list': 'List View',
        'timeline_view': 'Timeline',
        'agenda_view': 'Agenda View'
      };
      return typeLabels[displayType] || 'Schedule';
    },
    
    getDisplayClass(uiSuggestions) {
      if (!uiSuggestions) return 'display-list';
      
      const displayType = uiSuggestions.display_type || 'schedule_list';
      return `display-${displayType.replace('_', '-')}`;
    },

    handleGroupingChange(grouping) {
      this.currentGrouping = grouping;
    },

    getGroupedSessions(sessions) {
      if (!sessions || this.currentGrouping === 'none') return {};

      const groups = {};
      
      sessions.forEach(session => {
        let groupKey = 'Other';
        
        switch (this.currentGrouping) {
          case 'time':
            groupKey = this.getTimeGroup(session.start_time);
            break;
          case 'location':
            groupKey = session.location || 'No Location';
            break;
          case 'type':
            groupKey = session.session_type || 'General';
            break;
          case 'track':
            groupKey = session.track || 'Main Track';
            break;
        }
        
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(session);
      });

      // Sort sessions within each group by start time
      Object.keys(groups).forEach(key => {
        groups[key].sort((a, b) => {
          const timeA = a.start_time || '';
          const timeB = b.start_time || '';
          return timeA.localeCompare(timeB);
        });
      });

      return groups;
    },

    getTimeGroup(startTime) {
      if (!startTime) return 'No Time';
      
      const hour = parseInt(startTime.split(':')[0]);
      
      if (hour < 9) return 'Early Morning (Before 9 AM)';
      if (hour < 12) return 'Morning (9 AM - 12 PM)';
      if (hour < 14) return 'Lunch Time (12 PM - 2 PM)';
      if (hour < 17) return 'Afternoon (2 PM - 5 PM)';
      return 'Evening (After 5 PM)';
    },
    
    handleSessionAction(actionData) {
      this.$emit('session-action', actionData);
    },
    
    handleSessionClick(session) {
      this.$emit('session-click', session);
    },
    
    handleSpeakerClick(speakerData) {
      this.$emit('speaker-click', speakerData);
    },
    
    handleTagClick(tagData) {
      this.$emit('tag-click', tagData);
    },

    handleFollowUpAction(action) {
      this.$emit('action-click', action);
    }
  },
  
  mounted() {
    // Set default grouping based on UI suggestions
    if (this.response?.ui_suggestions?.default_grouping) {
      this.currentGrouping = this.response.ui_suggestions.default_grouping;
    }
  }
};
</script>

<style scoped>
.schedule-search-results {
  width: 100%;
}

.search-metadata {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 0.85rem;
}

.metadata-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.metadata-row:last-child {
  margin-bottom: 0;
}

.metadata-label {
  font-weight: 600;
  color: #495057;
  margin-right: 8px;
  min-width: 80px;
}

.metadata-value {
  color: #212529;
  font-weight: 500;
}

.search-time {
  color: #6c757d;
  font-size: 0.8rem;
  margin-left: 4px;
}

.grouping-options {
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
}

.grouping-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.grouping-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.grouping-btn {
  padding: 4px 12px;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  background-color: #fff;
  color: #495057;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.grouping-btn:hover {
  border-color: #adb5bd;
  background-color: #f8f9fa;
}

.grouping-btn.active {
  background-color: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

.results-container {
  margin-bottom: 16px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.results-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
}

.display-options {
  font-size: 0.8rem;
  color: #6c757d;
}

.display-type {
  font-weight: 500;
}

.grouped-results {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.session-group {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.group-title {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
}

.group-icon {
  margin-right: 8px;
  display: flex;
  align-items: center;
  color: #6c757d;
}

.group-count {
  margin-left: auto;
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
}

.group-sessions {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ungrouped-results .results-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Grid display for schedule_grid */
.results-list.display-schedule-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

/* Timeline display */
.results-list.display-timeline-view {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-left: 3px solid #0d6efd;
  padding-left: 12px;
}

.no-results {
  text-align: center;
  padding: 24px;
  color: #6c757d;
}

.no-results-icon {
  margin-bottom: 12px;
  color: #adb5bd;
}

.no-results h4 {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  color: #495057;
}

.no-results p {
  margin: 0;
  font-size: 0.9rem;
}

.schedule-insights {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
}

.insights-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insights-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.insight-item {
  font-size: 0.85rem;
  line-height: 1.4;
}

.insight-item strong {
  color: #495057;
  margin-right: 6px;
}

.insight-item span {
  color: #6c757d;
}

.follow-up-actions {
  margin-top: 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .results-list.display-schedule-grid {
    grid-template-columns: 1fr;
  }
  
  .results-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .search-metadata {
    padding: 10px;
  }
  
  .metadata-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .metadata-label {
    min-width: auto;
    margin-right: 0;
    margin-bottom: 2px;
  }

  .grouping-buttons {
    flex-direction: column;
  }

  .grouping-btn {
    width: 100%;
    text-align: center;
  }

  .group-title {
    padding: 10px 12px;
    font-size: 0.85rem;
  }

  .group-sessions {
    padding: 8px;
  }
}

@media (max-width: 480px) {
  .insights-grid {
    gap: 6px;
  }
  
  .insight-item {
    font-size: 0.8rem;
  }
}
</style>